import React, { useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { map, isEmpty } from 'underscore';
import { ValidatedLanguageTarget,
  ValidatedLocationTargetSetting,
  LanguageTargetValidatorFactory,
  AudienceSignalPanel } from '@bingads-webui-campaign-react/step-wizard-controls';
import { withUnvalidatedControlErrors, withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import {
  Collapse,
  TextTitle,
  useCollapseState,
  collapseExpander,
  ValidatedPanel,
  getPanelErrorProps
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { useFormService } from '@bingads-webui-react/form';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  LocationImg,
  LanguageImg,
  apiErrorMapper,
  campaignLanguageStrings,
  SearchThemesImg,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { AudienceSignalSection,
  getLocationTargetTitleDetails,
  getSearchThemeDetails,
  PANEL_KEYS } from '@bingads-webui-campaign-react/wizard-shared-components';
import { ValidationSearchThemeSelector } from '@bingads-webui-campaign-react/search-theme-selector';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  FormErrorGroup, UnmappedFormErrors as OriginalUnmappedFormErrors,
  entityIs, andComp, propertyIs, anyComp,
} from '@bingads-webui-campaign-react/form-error';
import { Campaign, AssetGroup } from '@bingads-webui/campaign-odata-schemas';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getLocationTargetingStyles } from '@bingads-webui-campaign-react/location-targeting';

import { ERROR_ENTITY } from '../../shared/model/api-error';
import { SectionShimmer, SingleLineShimmer } from '../../shared/components/section-shimmer';


const getTargetingStyles = () => ({
  container: {
    maxWidth: 680,
    '& .title-item': {
      width: 'unset !important',
    },
    '& .specific-location-targets-editor': {
      marginLeft: '-25px !important',
    },
    '& .intent-target ul': {
      paddingLeft: '20px',
    },
  },
  header: {
    marginBottom: 20,
  },
  ...getLocationTargetingStyles(),
});

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const campaignLocationCriterions = anyComp(
  entityIs(ERROR_ENTITY.CampaignLocation),
  entityIs(ERROR_ENTITY.CampaignRadius),
  entityIs(ERROR_ENTITY.CampaignNegativeLocation),
  entityIs(ERROR_ENTITY.CampaignLocationIntent)
);
const campaignLanguagesProp = andComp(entityIsCampaign, propertyIs('Languages'));

const ServerValidatedLocationTargetSetting = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLocationTargetSetting, { filter: campaignLocationCriterions }));
const ServerValidatedLanguageTarget = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLanguageTarget, { filter: campaignLanguagesProp }));
const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);

export const TargetingStepShell = ({
  data: targetingInfo = {},
  onDataChange,
  error,
}) => {
  const isLoading = useMemo(() => isEmpty(targetingInfo), [targetingInfo]);

  const classes = useDefaultStyles(getTargetingStyles);

  const { serverErrors, systemErrors } = error || {};

  const { state: { context: { saveFailed } } } = useFormService();

  const serverContext = useContext(ServerContext);
  const perfMarker = useContext(PerformanceMarkerContext);

  const {
    CurrentLocale: locale,
    DynamicPermissions,
    Permissions,
  } = serverContext;

  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const {
    bundleCountries,
    bundleLocationCriterions,
    campaign = {},
    assetGroup = {},
  } = targetingInfo || {};

  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext) || {};

  const isSearchThemeEnabled = permissions && permissions.dynamic && permissions.dynamic.SearchThemes;

  const [
    allAssetGroupSections,
    assetGroupExpandedSections,
    setAssetGroupExpandedSections,
  ] = useCollapseState(
    isSearchThemeEnabled ? [PANEL_KEYS.AUDIENCE_SIGNAL, 'search-themes'] : [PANEL_KEYS.AUDIENCE_SIGNAL] /* all sections */,
    isSearchThemeEnabled ? [PANEL_KEYS.AUDIENCE_SIGNAL, 'search-themes'] : [PANEL_KEYS.AUDIENCE_SIGNAL] /* initially expanded sections */
  );

  const [
    allCampaignSettingsSections,
    campaignSettingsExpandedSections,
    setCampaignSettingsExpandedSections,
  ] = useCollapseState(
    ['locations', 'language-target'],
    ['locations', 'language-target']
  );

  const campaignSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setCampaignSettingsExpandedSections }),
    [setCampaignSettingsExpandedSections]
  );

  const locationPanel = useMemo(() => (
    <div className={classes.LocationTargeting}>
      <ServerValidatedLocationTargetSetting
        validationProps={{
          data: campaign && campaign.locationTargets,
        }}
        bundleCountries={bundleCountries}
        bundleLocationCriterions={bundleLocationCriterions}
        scope={scope}
        bidStrategyType={campaign && campaign.DisplayBiddingStrategyType}
      />
    </div>
  ), [bundleCountries, bundleLocationCriterions, campaign, classes.LocationTargeting, scope]);

  const searchThemeSection = useMemo(() => (
    <div className={classNames('asset-group-search-theme-section')}>
      {
          isLoading ? <SingleLineShimmer /> :
            (<ValidationSearchThemeSelector
              i18n={i18n}
              wizardPerfMarker={perfMarker}
              searchThemes={assetGroup && assetGroup.AssetGroupSearchThemes}
            />)
      }
    </div>
  ), [assetGroup, i18n, isLoading, perfMarker]);

  const onAssetGroupSettingsValidate = useCallback((result) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroup: {
          ...prevData.assetGroup,
          ...result.data,
        },
      }),
      isValid: result.isValid,
      isDirty: result.isDirty,
    });
  }, [onDataChange]);

  const onCampaignSettingsValidate = useCallback((result) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        campaign: {
          ...prevData.campaign,
          ...result.data,
        },
      }),
      isValid: result.isValid,
      isDirty: result.isDirty,
    });
  }, [onDataChange]);

  return (
    <ActuationGroup actuationProps={{ isActive: saveFailed }}>
      <AttentionBeaconGroup>
        <FormErrorGroup formErrorProps={{ errors: serverErrors, saveFailed, errorMapper: apiErrorMapper }}>
          <div className={classNames(classes.container, 'targeting-step')}>
            <div className={classes.header}>
              <h1> {i18n.getString(_TL_('Who do you want to see your ads?'))} </h1>
              <span>{i18n.getString(_TL_('Tell us how and when you\'d like to optimize placements for your ads'))}</span>
            </div>
            <UnmappedFormErrors className="other-server-errors" />
            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>

            <ValidationGroup
              validationProps={{
                schema: Campaign,
                onValidate: onCampaignSettingsValidate,
                data: targetingInfo.campaign,
                i18n,
              }}
            >
              <Collapse
                allPanelItems={allCampaignSettingsSections}
                checkItems
                skipItemsTypeCheck
                activeKeys={campaignSettingsExpandedSections}
                setActiveKeys={setCampaignSettingsExpandedSections}
              >
                <ValidatedPanel
                  key="locations"
                  title={<TextTitle
                    titleKey={_TL_('Location')}
                    topicKey="pop_BA_Settings_LocationTargeting"
                    icon={LocationImg}
                    details={
                      getLocationTargetTitleDetails(
                        i18n,
                        campaign && campaign.locationTargets,
                        false,
                        campaign && campaign.BiddingScheme && campaign.BiddingScheme.Type,
                        true
                      )}
                  />}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                      expand: campaignSettingsExpand,
                      expandKey: 'locations',
                    }}
                >
                  {isLoading ? <SectionShimmer /> : locationPanel}
                </ValidatedPanel>
                <ValidatedPanel
                  key="language-target"
                  title={<TextTitle
                    titleKey={_TL_('Language')}
                    topicKey="pop_BA_PMAX_Targeting_Language"
                    details={
                      campaign && map(
                        campaign.Languages,
                        lang => i18n.getString(campaignLanguageStrings[lang] || lang)
                      ).toString()}
                    icon={LanguageImg}
                  />}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expand: campaignSettingsExpand,
                    expandKey: 'language-target',
                  }}
                >
                  {
                    isLoading ? <SectionShimmer /> : (
                      <ServerValidatedLanguageTarget
                        locale={locale}
                        permissions={permissions}
                        validationProps={{
                          field: 'Languages',
                          validator: LanguageTargetValidatorFactory(i18n),
                        }}
                      />
                    )
                  }
                </ValidatedPanel>
              </Collapse>
            </ValidationGroup>
            <ValidationGroup
              validationProps={{
                schema: AssetGroup,
                onValidate: onAssetGroupSettingsValidate,
                data: targetingInfo.assetGroup,
                i18n,
              }}
            >
              <AudienceSignalSection>
                <Collapse
                  allPanelItems={allAssetGroupSections}
                  checkItems
                  skipItemsTypeCheck
                  activeKeys={assetGroupExpandedSections}
                  setActiveKeys={setAssetGroupExpandedSections}
                >
                  <AudienceSignalPanel
                    key={PANEL_KEYS.AUDIENCE_SIGNAL}
                    isLoading={isLoading}
                    options={{
                      wizardPerfMarker: perfMarker,
                    }}
                    data={assetGroup}
                  />
                </Collapse>
              </AudienceSignalSection>
              {isSearchThemeEnabled &&
                <Collapse
                  allPanelItems={allAssetGroupSections}
                  checkItems
                  skipItemsTypeCheck
                  activeKeys={assetGroupExpandedSections}
                  setActiveKeys={setAssetGroupExpandedSections}
                >
                  <ValidatedPanel
                    key="search-themes"
                    title={<TextTitle
                      titleKey={_TL_('Search Themes')}
                      topicKey="pop_BA_PMAX_SearchThemes"
                      details={getSearchThemeDetails(i18n, assetGroup && assetGroup.AssetGroupSearchThemes)}
                      icon={SearchThemesImg}
                    />}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    actuationProps={{
                      expandKey: 'search-themes',
                    }}
                  >
                    {searchThemeSection}
                  </ValidatedPanel>
                </Collapse>
            }
            </ValidationGroup>
          </div>
        </FormErrorGroup>
      </AttentionBeaconGroup>
    </ActuationGroup>


  );
};

TargetingStepShell.propTypes = {
  data: PropTypes.shape({
    locationTargets: PropTypes.shape({}),
    bundleCountries: PropTypes.shape({}),
    bundleLocationCriterions: PropTypes.shape({}),
    campaign: PropTypes.shape({}),
    assetGroup: PropTypes.shape({}),
  }).isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  scope: PropTypes.shape({}).isRequired,
};

